import React from 'react'
import { useQuery } from '@apollo/client'
import { Link, useParams } from 'react-router-dom'
import { Header, Icon, Label, Message, Table } from 'semantic-ui-react'
import { LOAD_AUTH_USERS_QUERY } from '../../../graphql/admin'
import LoadingSpinner from '../../LoadingSpinner'
import AuthUserActions from './AuthUserActions'
import {
    getAuthEvent,
    formatDate,
    getInitials,
    getReferralPartner,
    getTokenStatus,
    getUserDisplayName,
} from './helpers'
import '../styles.css'

const DetailItem = ({ label, children }) => (
    <div className="auth-user-detail-item">
        <div className="auth-user-detail-label">{label}</div>
        <div className="auth-user-detail-value">{children}</div>
    </div>
)

const StatusLabel = ({ active, activeText, inactiveText }) => (
    <Label color={active ? 'green' : 'grey'} size="small">
        {active ? activeText : inactiveText}
    </Label>
)

const AuthHistory = ({ tokens = [] }) => {
    if (!tokens.length) {
        return (
            <Message
                info
                content="No auth token or session history has been recorded for this user yet."
            />
        )
    }

    return (
        <Table basic="very" className="auth-user-history-table">
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Event</Table.HeaderCell>
                    <Table.HeaderCell>When</Table.HeaderCell>
                    <Table.HeaderCell>Expires</Table.HeaderCell>
                    <Table.HeaderCell>IP</Table.HeaderCell>
                    <Table.HeaderCell>User agent</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {tokens.map((token) => {
                    const status = getTokenStatus(token)
                    const event = getAuthEvent(token)
                    return (
                        <Table.Row key={token.id}>
                            <Table.Cell>
                                <Label
                                    basic
                                    color={status === 'Active' ? 'green' : 'grey'}
                                    size="small"
                                >
                                    {status}
                                </Label>
                            </Table.Cell>
                            <Table.Cell>
                                <strong>{event.name}</strong>
                                <div className="auth-user-muted">
                                    {event.detail}
                                </div>
                            </Table.Cell>
                            <Table.Cell>{formatDate(token.createdAt)}</Table.Cell>
                            <Table.Cell>{formatDate(token.expiresAt)}</Table.Cell>
                            <Table.Cell>
                                <code>{token.ip || 'Unknown'}</code>
                            </Table.Cell>
                            <Table.Cell className="auth-user-user-agent">
                                {token.userAgent || 'Unknown'}
                            </Table.Cell>
                        </Table.Row>
                    )
                })}
            </Table.Body>
        </Table>
    )
}

const AuthUserDetail = () => {
    const { userId } = useParams()
    const { data, loading, error, refetch } = useQuery(LOAD_AUTH_USERS_QUERY, {
        variables: {
            query: userId,
            pagination: { page: 1, per_page: 1 },
        },
        fetchPolicy: 'cache-and-network',
    })

    if (loading && !data) return <LoadingSpinner />
    if (error) return <Message negative content={error.message} />

    const record = data?.authUsers?.users?.find(
        (authUserRecord) => authUserRecord.user.id === userId
    )

    if (!record) {
        return (
            <Message
                warning
                header="User not found"
                content="The user may have been deleted or the account id may be incorrect."
            />
        )
    }

    const user = record.user

    return (
        <div className="auth-users-page">
            <Link to="/admin/users" className="auth-user-back-link">
                <Icon name="arrow left" />
                Back to users
            </Link>

            <div className="auth-user-detail-header">
                <div className="auth-user-avatar auth-user-avatar-large">
                    {getInitials(user)}
                </div>
                <div className="auth-user-detail-title">
                    <Header as="h2">{getUserDisplayName(user)}</Header>
                    <div className="auth-user-muted">{user.email}</div>
                    <div>
                        <span className="auth-user-id">user_id: {user.id}</span>
                    </div>
                </div>
                <AuthUserActions record={record} onCompleted={refetch} />
            </div>

            <div className="auth-user-detail-section">
                <div className="auth-user-detail-grid">
                    <DetailItem label="Email">
                        {user.email}{' '}
                        <StatusLabel
                            active={record.emailVerified}
                            activeText="Verified"
                            inactiveText="Unverified"
                        />
                    </DetailItem>
                    <DetailItem label="Signed up">
                        {formatDate(record.signedUpAt)}
                    </DetailItem>
                    <DetailItem label="Referred by partner">
                        {getReferralPartner(user) || (
                            <span className="auth-user-muted">None</span>
                        )}
                    </DetailItem>
                    <DetailItem label="Latest login">
                        {formatDate(record.lastLoginAt, 'Never')}
                    </DetailItem>
                    <DetailItem label="Password">
                        <StatusLabel
                            active={record.passwordConfigured}
                            activeText="Configured"
                            inactiveText="Not configured"
                        />
                        <div className="auth-user-muted">
                            Updated {formatDate(record.passwordUpdatedAt)}
                        </div>
                    </DetailItem>
                    <DetailItem label="Account access">
                        <StatusLabel
                            active={!record.isBlocked}
                            activeText="Active"
                            inactiveText="Blocked"
                        />
                        {record.isBlocked && (
                            <div className="auth-user-muted">
                                {record.blockedReason || 'No reason recorded'}
                            </div>
                        )}
                    </DetailItem>
                    <DetailItem label="Failed login attempts">
                        {record.failedLoginAttempts || 0}
                    </DetailItem>
                    <DetailItem label="Locked until">
                        {formatDate(record.lockedUntil, 'Not locked')}
                    </DetailItem>
                    <DetailItem label="Email verified at">
                        {formatDate(record.emailVerifiedAt)}
                    </DetailItem>
                </div>
            </div>

            <div className="auth-user-detail-section">
                <Header as="h3">Auth History</Header>
                <AuthHistory tokens={record.recentTokens} />
            </div>
        </div>
    )
}

export default AuthUserDetail
